import { Suspense, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import { hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';

import { OutrightsCouponProvider } from './context/OutrightsCouponContext';
import { OutrightsCoupon } from './OutrightsCoupon';
import type { OutrightsCouponIndexProps } from './types';
import { breakpoints } from './utils';

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

function OutrightsCouponWithQuery({ dehydratedState, deviceType, nocache, ...props }: OutrightsCouponIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  const isoRender = (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <OutrightsCouponProvider {...props}>
          <I18nProvider i18nInstance={props.i18n}>
            <Suspense fallback={<DefaultLoadingFallback />}>
              <OutrightsCoupon {...props} />
            </Suspense>
          </I18nProvider>
        </OutrightsCouponProvider>
      </QueryClientProvider>
    </ReakitProvider>
  );

  if (typeof window !== 'undefined') {
    return isoRender;
  }

  const deviceWidth =
    deviceType === 'MOBILE' ? breakpoints.phoneSm : deviceType === 'TABLET' ? breakpoints.tablet : breakpoints.desktop;

  return <ResponsiveContext.Provider value={{ width: deviceWidth }}>{isoRender}</ResponsiveContext.Provider>;
}

export { OutrightsCouponWithQuery };
export type { OutrightsCouponIndexProps } from './types';
